import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router";
import AuthHeader from "../../layout/AuthHeader";
import PoweredBy from "../../layout/PoweredBy";
import AuthenticationService from "../../../services/AuthenticationService";
import Alert from "../../Alert";
import Button from "../../Button";
import Container from "../../Container";
import Input from "../../Input";
import UpdatePassword from "../../forms/UpdatePassword";
import { api } from "@services/apiRequest";
import { useSetPageTitle } from "@services/HeaderContext";

const Registration = () => {
    const intl = useIntl();
    const { token } = useParams();
    const [uid, setUid] = useState();
    const [email, setEmail] = useState("");
    const [fullName, setFullName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isValid, setIsValid] = useState(false);
    const [fullNameError, setFullNameError] = useState("");

    const [toc, setTOC] = useState(false);

    useSetPageTitle("Registration");

    useEffect(() => {
        setLoading(true);
        api
            .post("/users/validate", { token: token }, { publicRequest: true })
            .then(({ data }) => {
                setUid(data.id);
                setEmail(data.email);
                setFullName(data.full_name || "");
            })
            .catch((err) => {
                console.error("Error:", err);
                setError("Not authorized");
                setIsValid(false);
            })
            .finally(() => setLoading(false));
    }, [token]);

    const validateFullName = (name) => {
        if (name.length < 3) {
            setFullNameError(intl.formatMessage({ id: "full_name_input_error" }));
            return false;
        }
        setFullNameError("");
        return true;
    };

    const handleFullNameChange = (e) => {
        const name = e.target.value;
        setFullName(name);
        setIsValid(validateFullName(name) && password.length > 0);
    };

    const submit = useCallback(
        async (e) => {
            e.preventDefault();
            if (!isValid) return;

            setLoading(true);
            setError("");
            try {
                const { data: response } = await api.post(
                    "/users/signup",
                    { uid: uid, password: password, full_name: fullName },
                    { publicRequest: true }
                );

                if (response.errors) {
                    setError(intl.formatMessage({ id: "form_error" }));
                    return;
                }

                await AuthenticationService.login(email, password);
                window.location = "/home";
            } catch (error) {
                setError(intl.formatMessage({ id: "error_while_saving" }));
            } finally {
                setLoading(false);
            }
        },
        [password, uid, email, fullName, intl, isValid]
    );

    return (
        <div className="bg-am-400 min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <Container>
                <AuthHeader title="Registrazione utente" />

                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow sm:rounded-3xl sm:px-10">
                        <form action="#" method="POST" onSubmit={submit}>
                            <div>
                                <Input
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    label="Email"
                                    type="email"
                                    disabled
                                />
                            </div>
                            <div>
                                <Input
                                    required
                                    value={fullName}
                                    onChange={handleFullNameChange}
                                    label={intl.formatMessage({ id: "full_name" })}
                                    type="text"
                                    error={fullNameError}
                                />
                                {fullNameError && (
                                    <div className="text-red-500 text-sm mb-4">
                                        {fullNameError}
                                    </div>
                                )}
                            </div>

                            <UpdatePassword
                                password_label_id="Password"
                                password={password}
                                setPassword={setPassword}
                                confirmPassword={confirmPassword}
                                setConfirmPassword={setConfirmPassword}
                                onValid={(valid) => setIsValid(validateFullName(fullName) && valid)}
                            />

                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center">
                                    <input
                                        id="show-toc"
                                        name="show-toc"
                                        type="checkbox"
                                        className="h-4 w-4 text-am-600 focus:ring-am-500 border-gray-300 rounded"
                                        onChange={(e) => {
                                            setTOC(e.target.checked);
                                        }}
                                    />
                                    <label
                                        htmlFor="show-toc"
                                        className="ml-2 block text-sm text-gray-900"
                                    >
                                        <FormattedMessage
                                            id="terms_and_conditions"
                                            values={{
                                                a: (chunks) => (
                                                    <a href="/terms" target="_blank" rel="noopener noreferrer">
                                                        {chunks}
                                                    </a>
                                                ),
                                            }}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div>
                                <Button
                                    full
                                    type="submit"
                                    submitting={loading}
                                    disabled={!isValid || !toc}
                                >
                                    <FormattedMessage id="signup" />
                                </Button>
                            </div>
                            {error && (
                                <div className="mt-4">
                                    <Alert slim type="error" title={error} />
                                </div>
                            )}
                        </form>
                    </div>
                    <PoweredBy />
                </div>
            </Container>
        </div>
    );
};

export default Registration;
